<template>
    <div>
        <entity-reader
            entity="encounter"
            :id="id"
            api-name="encounters"
            :use-sharing="true"
            :allow-copy="true"
            home-route-name="encounterTimeTable"
            @approve="handleApprove"
        >
            <template v-slot:[`reader`]="{ doc }">
                <v-row>
                    <v-col cols="12" sm="6">
                        <label-value name="Event/Day/Time" :value="getEventDayTime(doc)" name-cols="4" value-cols="8" />
                        <label-value
                            name="Encounter Type"
                            :value="doc.encounterTypeText"
                            name-cols="4"
                            value-cols="8"
                        />
                        <label-value name="Priority" :value="doc.priorityText" name-cols="4" value-cols="8" />
                        <label-value name="Ref Cover Needed" :value="doc.refCoverText" name-cols="4" value-cols="8" />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <span class="font-weight-medium">Tags</span>
                        <div class="mt-2">
                            <v-chip v-for="(tag, i) in doc.tags" :key="i" class="mr-2 px-4">
                                {{ tag }}
                            </v-chip>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" sm="2">
                        <span class="font-weight-medium">Plot</span>
                    </v-col>
                    <v-col cols="12" sm="10">
                        <router-link
                            :to="{ name: 'readPlot', params: { id: doc.plotKeyId } }"
                            :title="doc.plotName"
                            class="mr-2"
                        >
                            {{ doc.plotName }}
                        </router-link>
                        <approval-status :status="doc.plotStatus"></approval-status>
                    </v-col>
                </v-row>
                <label-value name="Name" :value="doc.name" />
                <label-value name="Inbound" :value="getInbound(doc)" />
                <label-value name="Outbound" :value="getOutbound(doc)" />
                <label-value name="Description" :value="doc.description" />
                <label-value name="Costume Level" :value="doc.costumeLevelText" />
                <label-value name="Costume Notes" :value="doc.costumeNotes" />
                <label-value name="Makeup Level" :value="doc.makeupLevelText" />
                <label-value name="Makeup" :value="doc.makeupNotes" />
                <v-row>
                    <v-col cols="12" sm="2"><span class="font-weight-medium">Monsters</span></v-col>
                    <v-col cols="12" sm="10">
                        <v-expansion-panels accordion flat outlined>
                            <v-expansion-panel v-for="(monster, i) in doc.monsters" :key="i">
                                <v-expansion-panel-header>
                                    <span>
                                        <span class="mr-2">
                                            {{ monster.minQuantity }} - {{ monster.maxQuantity }} x
                                            {{ monster.stats.name }}
                                        </span>
                                        <approval-status :status="monster.stats.statusId"></approval-status>
                                    </span>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content>
                                    <monster-summary :monster="monster.stats"></monster-summary>
                                    <div v-if="monster.notes">
                                        <span class="font-weight-medium">Notes:</span>
                                        {{ monster.notes }}
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-col>
                </v-row>
            </template>
        </entity-reader>
        <v-dialog v-model="showApprovalFailureDialog" max-width="900">
            <v-card tile data-card>
                <v-toolbar dark color="warning">
                    <v-btn icon dark data-close @click="showApprovalFailureDialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    <v-toolbar-title>Cannot Approve Encounter</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="text-h6">
                    The following things are not approved
                    <ul>
                        <li v-for="(val, i) in approvalValidation" :key="i">
                            {{ val }}
                        </li>
                    </ul>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>
<script>
import api from '@/api'
import { mapGetters } from 'vuex'
import { DateTime } from 'luxon'
import EntityReader from '../../components/EntityRead'
import LabelValueRender from '../../components/LabelValueRender'
import MonsterSummary from '../../components/MonsterSummary'
import ApprovalStatusIcon from '../../components/ApprovalStatusIcon'
import editingStatuses from '../../components/editingStatuses'
export default {
    name: 'EncounterReader',
    components: {
        'entity-reader': EntityReader,
        'label-value': LabelValueRender,
        'monster-summary': MonsterSummary,
        'approval-status': ApprovalStatusIcon,
    },
    props: {
        id: {
            type: [Number, String],
            required: true,
            validator: value => {
                return !!value
            },
        },
    },
    data() {
        return {
            showApprovalFailureDialog: false,
            approvalValidation: [],
        }
    },
    computed: {
        ...mapGetters(['events']),
    },
    methods: {
        getEventDayTime(doc) {
            if (!doc.eventId || !this.events || this.events.length === 0) return ''
            let event = this.events.find(x => x.id === doc.eventId)
            return `${event.name || ''} ${this.getTimeStamp(doc)}`
        },
        getTimeStamp(doc) {
            if (!this.isValidDate(doc.day.date)) return
            let dateOnly = DateTime.fromISO(doc.day.date, { zone: 'utc' })
            if (doc.timeAsMinutes) {
                let dateTime = dateOnly.plus({ minutes: doc.timeAsMinutes })
                return `${dateOnly.toFormat('cccc')} ${dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)}`
            } else {
                return `${dateOnly.toFormat('cccc')} - Floating`
            }
        },
        isValidDate(value) {
            if (!value || value === 'Invalid DateTime' || (DateTime.isDateTime(value) && !value.isValid)) {
                return false
            }
            return true
        },
        getInbound(doc) {
            if (doc.inbound === 0) return 'None'
            if (doc.inbound === 1) return `Transport in from ${doc.inboundCircleName ?? 'Unknown'}`
            if (doc.inbound === 2) return `Walk in from ${doc.inboundDetails ?? 'Unknown'}`
            if (doc.inbound === 3) return `Appear from ${doc.inboundDetails ?? 'Unknown'}`
        },
        getOutbound(doc) {
            if (doc.outbound === 0) return 'None'
            if (doc.outbound === 1) return `Transport out to ${doc.outboundCircleName ?? 'Unknown'}`
            if (doc.outbound === 2) return `Walk out to ${doc.outboundDetails ?? 'Unknown'}`
            if (doc.outbound === 3) return `Disappear when ${doc.outboundDetails ?? 'Unknown'}`
        },
        handleApprove(doc) {
            let plotValidation = []
            if (!doc.plotStatus || doc.plotStatus !== 3) {
                plotValidation.push('Plot Line is not approved')
            }
            let monsterApprovals = doc.monsters
                .filter(x => x.stats.statusId !== 3)
                .map(x => `Monster '${x.stats.name}' is not approved`)
            this.approvalValidation = plotValidation.concat(monsterApprovals)
            if (this.approvalValidation.length > 0) {
                this.showApprovalFailureDialog = true
            } else {
                this.approve(doc)
            }
        },
        approve(doc) {
            this.isSaving = true
            var model = { version: doc.version, status: editingStatuses.Approved }
            api.patch(`/encounters/${doc.keyId}/status`, model)
                .then(() => {
                    this.showSnackbar('info', `Encounter Approved`)
                    this.$router.push({ name: 'encounterTimeTable' })
                    //window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
    },
}
</script>
